import { useState } from 'react'
import {
  Tabs,
  Table,
  Badge,
  Text,
  Group,
  UnstyledButton,
  Tooltip,
  Skeleton,
  Box,
  Button,
  ThemeIcon,
} from '@mantine/core'
import {
  IconServer,
  IconNetwork,
  IconTargetArrow,
  IconDatabase,
  IconWorld,
  IconBucket,
} from '@tabler/icons-react'
import type { TopologyNode } from '@/api/topology'
import { reasonLabel } from '@/utils/reasonLabels'

interface ResourceTabsProps {
  topologyNodes: TopologyNode[]
  cwMetrics: any
  onResourceClick?: (nodeId: string, nodeData: any) => void
  loading?: boolean
}

type ResourceType = 'ec2' | 'alb' | 'target_group' | 'rds' | 'domain' | 's3'

const STATUS_COLORS: Record<string, string> = {
  healthy: 'green',
  warning: 'yellow',
  critical: 'red',
}

function StatusDot({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'gray'
  const hex = color === 'green' ? '#2f9e44' : color === 'yellow' ? '#f59f00' : color === 'red' ? '#e03131' : '#868e96'
  return (
    <Box
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: hex,
        flexShrink: 0,
      }}
    />
  )
}

function TruncatedName({ name, onClick }: { name: string; onClick?: () => void }) {
  if (name.length <= 30) {
    return (
      <Text size="sm" fw={500} style={{ cursor: onClick ? 'pointer' : undefined }}>
        {name}
      </Text>
    )
  }
  return (
    <Tooltip label={name} withArrow>
      <Text
        size="sm"
        fw={500}
        style={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: onClick ? 'pointer' : undefined,
        }}
      >
        {name}
      </Text>
    </Tooltip>
  )
}

function MetricText({ value }: { value: string | number | null | undefined }) {
  return (
    <Text size="sm" ff="monospace" c={value == null ? 'dimmed' : undefined}>
      {value ?? '—'}
    </Text>
  )
}

const COLLAPSE_THRESHOLD = 5

function CollapsibleRows({
  rows,
  renderRow,
}: {
  rows: any[]
  renderRow: (item: any, index: number) => React.ReactNode
}) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? rows : rows.slice(0, COLLAPSE_THRESHOLD)
  const hidden = rows.length - COLLAPSE_THRESHOLD

  return (
    <>
      {visible.map((item, i) => renderRow(item, i))}
      {rows.length > COLLAPSE_THRESHOLD && (
        <Table.Tr>
          <Table.Td colSpan={10}>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setExpanded(prev => !prev)}
            >
              {expanded ? 'recolher' : `expandir (mais ${hidden})`}
            </Button>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

function EmptyRow({ message }: { message: string }) {
  return (
    <Table.Tr>
      <Table.Td colSpan={10}>
        <Text ta="center" c="dimmed" py="sm" size="sm">
          {message}
        </Text>
      </Table.Td>
    </Table.Tr>
  )
}

// --- EC2 Tab ---
function Ec2Tab({
  nodes,
  cwMetrics,
  onResourceClick,
}: {
  nodes: TopologyNode[]
  cwMetrics: any
  onResourceClick?: (nodeId: string, nodeData: any) => void
}) {
  const cw = cwMetrics?.ec2

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th></Table.Th>
          <Table.Th>Nome</Table.Th>
          <Table.Th>Tipo</Table.Th>
          <Table.Th>CPU%</Table.Th>
          <Table.Th>MEM</Table.Th>
          <Table.Th>Disk%</Table.Th>
          <Table.Th>Network ↓↑</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {nodes.length === 0 ? (
          <EmptyRow message="Nenhuma instância EC2 encontrada" />
        ) : (
          <CollapsibleRows
            rows={nodes}
            renderRow={(node, i) => (
              <Table.Tr
                key={node.id ?? i}
                style={{ cursor: onResourceClick ? 'pointer' : undefined }}
                onClick={() => onResourceClick?.(node.id, node.data)}
              >
                <Table.Td>
                  <StatusDot status={node.data.status} />
                </Table.Td>
                <Table.Td>
                  <TruncatedName
                    name={node.data.label}
                    onClick={onResourceClick ? () => onResourceClick(node.id, node.data) : undefined}
                  />
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color="blue">
                    {(node.data.instance_type as string) ?? 'ec2'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.cpu_avg != null ? `${cw.cpu_avg}%` : (node.data.cpu as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.memory as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.disk as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.network as string) ?? null} />
                </Table.Td>
              </Table.Tr>
            )}
          />
        )}
      </Table.Tbody>
    </Table>
  )
}

// --- ALB Tab ---
function AlbTab({
  nodes,
  cwMetrics,
  onResourceClick,
}: {
  nodes: TopologyNode[]
  cwMetrics: any
  onResourceClick?: (nodeId: string, nodeData: any) => void
}) {
  const cw = cwMetrics?.alb

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th></Table.Th>
          <Table.Th>Nome</Table.Th>
          <Table.Th>Latência</Table.Th>
          <Table.Th>Req/min</Table.Th>
          <Table.Th>5XX</Table.Th>
          <Table.Th>Targets</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {nodes.length === 0 ? (
          <EmptyRow message="Nenhum Load Balancer encontrado" />
        ) : (
          <CollapsibleRows
            rows={nodes}
            renderRow={(node, i) => (
              <Table.Tr
                key={node.id ?? i}
                style={{ cursor: onResourceClick ? 'pointer' : undefined }}
                onClick={() => onResourceClick?.(node.id, node.data)}
              >
                <Table.Td>
                  <StatusDot status={node.data.status} />
                </Table.Td>
                <Table.Td>
                  <TruncatedName name={node.data.label} />
                </Table.Td>
                <Table.Td>
                  <MetricText
                    value={
                      cw?.response_time_p95_ms != null
                        ? `${cw.response_time_p95_ms} ms`
                        : (node.data.latency as string) ?? null
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.requests_per_min ?? (node.data.requests_per_min as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.http_5xx ?? (node.data.http_5xx as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText
                    value={
                      cw?.healthy_targets != null && cw?.total_targets != null
                        ? `${cw.healthy_targets}/${cw.total_targets}`
                        : (node.data.targets as string) ?? null
                    }
                  />
                </Table.Td>
              </Table.Tr>
            )}
          />
        )}
      </Table.Tbody>
    </Table>
  )
}

// --- Target Groups Tab ---
function TargetGroupTab({
  nodes,
  onResourceClick,
}: {
  nodes: TopologyNode[]
  onResourceClick?: (nodeId: string, nodeData: any) => void
}) {
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th></Table.Th>
          <Table.Th>Nome</Table.Th>
          <Table.Th>Protocolo</Table.Th>
          <Table.Th>Porta</Table.Th>
          <Table.Th>Targets (saudável/total)</Table.Th>
          <Table.Th>Health check</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {nodes.length === 0 ? (
          <EmptyRow message="Nenhum Target Group encontrado" />
        ) : (
          <CollapsibleRows
            rows={nodes}
            renderRow={(node, i) => (
              <Table.Tr
                key={node.id ?? i}
                style={{ cursor: onResourceClick ? 'pointer' : undefined }}
                onClick={() => onResourceClick?.(node.id, node.data)}
              >
                <Table.Td>
                  <StatusDot status={node.data.status} />
                </Table.Td>
                <Table.Td>
                  <TruncatedName name={node.data.label} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.protocol as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.port as string | number) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText
                    value={
                      node.data.healthy_targets != null && node.data.total_targets != null
                        ? `${node.data.healthy_targets as number}/${node.data.total_targets as number}`
                        : (node.data.targets as string) ?? null
                    }
                  />
                </Table.Td>
                <Table.Td>
                  {node.data.worst_reason ? (
                    <Badge
                      size="xs"
                      variant="light"
                      color={
                        node.data.status === 'critical'
                          ? 'red'
                          : node.data.status === 'warning'
                            ? 'yellow'
                            : 'gray'
                      }
                    >
                      {reasonLabel(node.data.worst_reason as string)}
                    </Badge>
                  ) : (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
          />
        )}
      </Table.Tbody>
    </Table>
  )
}

// --- RDS Tab ---
function RdsTab({
  nodes,
  cwMetrics,
  onResourceClick,
}: {
  nodes: TopologyNode[]
  cwMetrics: any
  onResourceClick?: (nodeId: string, nodeData: any) => void
}) {
  const cw = cwMetrics?.rds

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th></Table.Th>
          <Table.Th>Nome</Table.Th>
          <Table.Th>Engine</Table.Th>
          <Table.Th>CPU%</Table.Th>
          <Table.Th>Conexões</Table.Th>
          <Table.Th>Memória (GB)</Table.Th>
          <Table.Th>Read Latency (ms)</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {nodes.length === 0 ? (
          <EmptyRow message="Nenhuma instância RDS encontrada" />
        ) : (
          <CollapsibleRows
            rows={nodes}
            renderRow={(node, i) => (
              <Table.Tr
                key={node.id ?? i}
                style={{ cursor: onResourceClick ? 'pointer' : undefined }}
                onClick={() => onResourceClick?.(node.id, node.data)}
              >
                <Table.Td>
                  <StatusDot status={node.data.status} />
                </Table.Td>
                <Table.Td>
                  <TruncatedName name={node.data.label} />
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color="orange">
                    {(node.data.engine as string) ?? 'rds'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.cpu != null ? `${cw.cpu}%` : (node.data.cpu as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.connections ?? (node.data.connections as string | number) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.freeable_memory_gb ?? (node.data.memory_gb as string | number) ?? null} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={cw?.read_latency_ms ?? (node.data.read_latency_ms as string | number) ?? null} />
                </Table.Td>
              </Table.Tr>
            )}
          />
        )}
      </Table.Tbody>
    </Table>
  )
}

// --- DNS Tab ---
function DnsTab({
  nodes,
  onResourceClick,
}: {
  nodes: TopologyNode[]
  onResourceClick?: (nodeId: string, nodeData: any) => void
}) {
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th></Table.Th>
          <Table.Th>Zona</Table.Th>
          <Table.Th>Visibilidade</Table.Th>
          <Table.Th>Registros</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {nodes.length === 0 ? (
          <EmptyRow message="Nenhuma zona DNS encontrada" />
        ) : (
          <CollapsibleRows
            rows={nodes}
            renderRow={(node, i) => (
              <Table.Tr
                key={node.id ?? i}
                style={{ cursor: onResourceClick ? 'pointer' : undefined }}
                onClick={() => onResourceClick?.(node.id, node.data)}
              >
                <Table.Td>
                  <StatusDot status={node.data.status} />
                </Table.Td>
                <Table.Td>
                  <TruncatedName name={node.data.label} />
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="xs"
                    variant="light"
                    color={(node.data.private as boolean) ? 'gray' : 'teal'}
                  >
                    {(node.data.private as boolean) ? 'privado' : 'público'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.record_count as string | number) ?? null} />
                </Table.Td>
              </Table.Tr>
            )}
          />
        )}
      </Table.Tbody>
    </Table>
  )
}

// --- S3 Tab ---
function S3Tab({
  nodes,
  onResourceClick,
}: {
  nodes: TopologyNode[]
  onResourceClick?: (nodeId: string, nodeData: any) => void
}) {
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th></Table.Th>
          <Table.Th>Bucket</Table.Th>
          <Table.Th>Região</Table.Th>
          <Table.Th>Versionamento</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {nodes.length === 0 ? (
          <EmptyRow message="Nenhum bucket S3 encontrado" />
        ) : (
          <CollapsibleRows
            rows={nodes}
            renderRow={(node, i) => (
              <Table.Tr
                key={node.id ?? i}
                style={{ cursor: onResourceClick ? 'pointer' : undefined }}
                onClick={() => onResourceClick?.(node.id, node.data)}
              >
                <Table.Td>
                  <StatusDot status={node.data.status} />
                </Table.Td>
                <Table.Td>
                  <TruncatedName name={node.data.label} />
                </Table.Td>
                <Table.Td>
                  <MetricText value={(node.data.region as string) ?? null} />
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="xs"
                    variant="light"
                    color={(node.data.versioning as string) === 'Enabled' ? 'green' : 'gray'}
                  >
                    {(node.data.versioning as string) ?? '—'}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            )}
          />
        )}
      </Table.Tbody>
    </Table>
  )
}

// --- Main Component ---
export function ResourceTabs({ topologyNodes, cwMetrics, onResourceClick, loading }: ResourceTabsProps) {
  const byType = topologyNodes.reduce<Record<string, TopologyNode[]>>((acc, node) => {
    const t = node.data.type as string
    if (!acc[t]) acc[t] = []
    acc[t].push(node)
    return acc
  }, {})

  const ec2Nodes = byType['ec2'] ?? []
  const albNodes = byType['alb'] ?? []
  const tgNodes = byType['target_group'] ?? []
  const rdsNodes = byType['rds'] ?? []
  const dnsNodes = byType['domain'] ?? []
  // s3 nodes may come with type 's3' even though it's not in the union — handle gracefully
  const s3Nodes = byType['s3'] ?? []

  const tabLabel = (label: string, count: number) => `${label} (${count})`

  if (loading) {
    return (
      <Box>
        <Skeleton height={36} mb="sm" />
        <Skeleton height={200} />
      </Box>
    )
  }

  return (
    <Tabs defaultValue="ec2" variant="outline">
      <Tabs.List>
        <Tabs.Tab
          value="ec2"
          leftSection={
            <ThemeIcon size="xs" variant="transparent" color="blue">
              <IconServer size={14} />
            </ThemeIcon>
          }
        >
          {tabLabel('EC2', ec2Nodes.length)}
        </Tabs.Tab>
        <Tabs.Tab
          value="alb"
          leftSection={
            <ThemeIcon size="xs" variant="transparent" color="indigo">
              <IconNetwork size={14} />
            </ThemeIcon>
          }
        >
          {tabLabel('Load Balancers', albNodes.length)}
        </Tabs.Tab>
        <Tabs.Tab
          value="target_group"
          leftSection={
            <ThemeIcon size="xs" variant="transparent" color="cyan">
              <IconTargetArrow size={14} />
            </ThemeIcon>
          }
        >
          {tabLabel('Target Groups', tgNodes.length)}
        </Tabs.Tab>
        <Tabs.Tab
          value="rds"
          leftSection={
            <ThemeIcon size="xs" variant="transparent" color="orange">
              <IconDatabase size={14} />
            </ThemeIcon>
          }
        >
          {tabLabel('RDS', rdsNodes.length)}
        </Tabs.Tab>
        <Tabs.Tab
          value="dns"
          leftSection={
            <ThemeIcon size="xs" variant="transparent" color="teal">
              <IconWorld size={14} />
            </ThemeIcon>
          }
        >
          {tabLabel('DNS', dnsNodes.length)}
        </Tabs.Tab>
        <Tabs.Tab
          value="s3"
          leftSection={
            <ThemeIcon size="xs" variant="transparent" color="yellow">
              <IconBucket size={14} />
            </ThemeIcon>
          }
        >
          {tabLabel('S3', s3Nodes.length)}
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="ec2" pt="sm">
        <Ec2Tab nodes={ec2Nodes} cwMetrics={cwMetrics} onResourceClick={onResourceClick} />
      </Tabs.Panel>

      <Tabs.Panel value="alb" pt="sm">
        <AlbTab nodes={albNodes} cwMetrics={cwMetrics} onResourceClick={onResourceClick} />
      </Tabs.Panel>

      <Tabs.Panel value="target_group" pt="sm">
        <TargetGroupTab nodes={tgNodes} onResourceClick={onResourceClick} />
      </Tabs.Panel>

      <Tabs.Panel value="rds" pt="sm">
        <RdsTab nodes={rdsNodes} cwMetrics={cwMetrics} onResourceClick={onResourceClick} />
      </Tabs.Panel>

      <Tabs.Panel value="dns" pt="sm">
        <DnsTab nodes={dnsNodes} onResourceClick={onResourceClick} />
      </Tabs.Panel>

      <Tabs.Panel value="s3" pt="sm">
        <S3Tab nodes={s3Nodes} onResourceClick={onResourceClick} />
      </Tabs.Panel>
    </Tabs>
  )
}
